import {ContentBlock, VisibilityOption} from './types';
import {ReviewCard} from './review-card';
import {getVisibleContentBlocks} from '../../checklist-availability';
import {GOALS_REFLECTION_PROMPT} from './goals-content';

interface PreviewContentCardProps {
  blocks: ContentBlock[];
  visibility: VisibilityOption;
  onEdit?: () => void;
  cardVariant?: 'default' | 'detail';
}

const VISIBILITY_LABELS: Record<Exclude<VisibilityOption, ''>, string> = {
  public: 'Public — everyone in the cycle',
  private: 'Private — manager and employee only',
  anonymous: 'Anonymous — responses are anonymized',
};

const getBlockDetails = (block: ContentBlock) => {
  if (block.type === 'question') {
    return {type: 'Q', color: 'text-green300', label: block.questionData.text};
  }
  if (block.type === 'checklist') {
    return {type: 'C', color: 'text-blue300', label: block.checklistData.title};
  }
  if (block.type === 'goals') {
    return {
      type: 'G',
      color: 'text-purple300',
      label: GOALS_REFLECTION_PROMPT,
    };
  }
  return {type: 'P', color: 'text-orange300', label: block.pulseData.name};
};

export const PreviewContentCard = ({
  blocks,
  visibility,
  onEdit,
  cardVariant,
}: PreviewContentCardProps) => {
  const visibleBlocks = getVisibleContentBlocks(blocks);

  return (
    <ReviewCard title="Content" onEdit={onEdit} variant={cardVariant}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 border-b border-borderLight pb-4 sm:flex-row sm:justify-between">
          <span className="text-sm font-medium leading-[22px] text-textBody">
            Visibility
          </span>
          <span className="text-sm leading-[22px] text-blackTwo sm:text-right">
            {visibility ? VISIBILITY_LABELS[visibility] : '—'}
          </span>
        </div>
        <div className="flex items-start gap-4 sm:gap-[65px]">
          <span className="shrink-0 text-sm font-medium leading-[22px] text-textBody">
            Blocks
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {visibleBlocks.length ? (
              visibleBlocks.map((block) => {
                const details = getBlockDetails(block);
                return (
                  <div
                    key={block.id}
                    className="flex min-w-0 items-start gap-4 rounded-[10px] bg-backgroundLight px-4 py-2">
                    <span
                      className={`shrink-0 text-sm font-medium leading-[22px] ${details.color}`}>
                      {details.type}
                    </span>
                    <span className="min-w-0 break-words text-sm leading-[22px] text-blackTwo">
                      {details.label || 'Untitled block'}
                    </span>
                  </div>
                );
              })
            ) : (
              <span className="text-sm leading-[22px] text-textBody">
                No active content blocks.
              </span>
            )}
          </div>
        </div>
      </div>
    </ReviewCard>
  );
};
